/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { MicroAppBase } from './micro-app-base';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { UtilService } from '@app/util/util.service';

/**
 * Window implementation of a micro app
 * Handles window-specific functionality for micro applications
 * @template T The type of the status data
 */
export class WindowMicroApp<T = any> extends MicroAppBase<T> {
  private window: Window | null = null;

  constructor(
    appID: string,
    url: URL,
    sessionID: string,
    postMessageService: PostMessengerService,
    private onClosed: (appId: string) => void
  ) {
    super('Window', appID, url, sessionID, postMessageService);
  }

  async launch(): Promise<void> {
    this.window = window.open(this.url.toString(), `MicroApp-${UtilService.getUUID()}`, 'popup');

    this.postMessageService.register({
      targetAppId: this.appID,
      targetAppWindow: this.window,
      targetOrigin: this.url.origin,
      onWindowUpdates: stats => {
        if (stats.isClosed) {
          this.onClosed(this.appID);
        }
      },
      onResponse: data => {
        this.updateStatus(data);
      }
    });

    this._minimized = false;
  }

  restore(): void {
    if (this.window && !this.window.closed) {
      this.window.focus();
      this._minimized = false;
    }
  }

  minimize(): void {
    // Windows don't have explicit minimize, but we can track the state
    this._minimized = true;
  }

  updateStatus(status: T): void {
    this._status.next(status);
  }

  close(): void {
    if (this.window) {
      this.window.close();
      this.window = null;
    }
    this.onClosed(this.appID);
    this.cleanup();
  }

  isActive(): boolean {
    return this.window !== null && !this.window.closed;
  }

  // Getter for window reference (for backward compatibility)
  get windowReference(): Window | null {
    return this.window;
  }
}
